import React, { useContext, useState } from 'react';
import {
  Box,
  IconButton,
  List,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { DigitalCase } from '../../../redux/types';
import SurveyOptionContext from '../../../views/admin/SurveyOptionContext';

type Props = {
  caseList: DigitalCase[];
  onAddCase: (caseName: string) => void
  onUpdateCase: (caseObj: DigitalCase) => void;
  onDeleteCase: (caseId: number) => void;
};

const CaseEditorList: React.FC<Props> = ({
  caseList,
  onAddCase,
  onUpdateCase,
  onDeleteCase,
}: Props) => {
  const [caseName, setCaseName] = useState<string>('');
  const [editCaseBuffer, setEditCaseBuffer] = useState<DigitalCase>();
  const { disableEdit } = useContext(SurveyOptionContext);

  const onCaseNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCaseName(e.target.value);
  };

  const onTextEnterHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && caseName.trim()) {
      onAddCase(caseName);
      setCaseName('');
    }
  };

  const onCaseBufferNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (editCaseBuffer) {
      setEditCaseBuffer({
        ...editCaseBuffer,
        name,
      });
    }
  };

  const editCaseHandler = (caseObj: DigitalCase) => {
    setEditCaseBuffer({ ...caseObj });
  };

  const cancelEditHandler = () => {
    setEditCaseBuffer(undefined);
  };

  const saveUpdateHandler = () => {
    if (editCaseBuffer) {
      onUpdateCase(editCaseBuffer);
    }
    setEditCaseBuffer(undefined);
  };

  const deleteCaseHandler = (cId: number) => {
    onDeleteCase(cId);
  };

  return (
    <Box>
      <List>
        {
          caseList.map((c) => (
            <Box key={c.id}>
              {
                editCaseBuffer && editCaseBuffer.id === c.id ? (
                  <Box display="flex" aria-label="case-edit" alignItems="center">
                    <Box sx={{ py: 1 }} flexGrow={1}>
                      <TextField onChange={onCaseBufferNameChange} value={editCaseBuffer.name} size="small" fullWidth label="Update Case" />
                    </Box>
                    <Box>
                      <IconButton onClick={cancelEditHandler}>
                        <CancelOutlinedIcon />
                      </IconButton>
                      <IconButton onClick={saveUpdateHandler}>
                        <CheckIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box display="flex" aria-label="case-view">
                    <Box sx={{ py: 1 }} flexGrow={1}>
                      <Typography>{c.name}</Typography>
                    </Box>
                    <Box>
                      <>
                        <IconButton disabled={disableEdit} onClick={() => editCaseHandler(c)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton disabled={disableEdit} onClick={() => deleteCaseHandler(c.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    </Box>
                  </Box>
                )
              }
            </Box>
          ))
        }
      </List>
      {
        !editCaseBuffer && (
          <Box aria-label="add-case">
            <TextField
              fullWidth
              size="small"
              label="Add Case"
              value={caseName}
              onChange={onCaseNameChange}
              onKeyDown={onTextEnterHandler}
              disabled={disableEdit}
            />
          </Box>
        )
      }
    </Box>
  );
};

export default CaseEditorList;
